import { unsafeCSS } from "lit";
import { watch } from "vue";
import mermaid, { RenderResult } from "mermaid";
import { EditorState, Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import { BaseExtension } from "@/wc/text-editor/lib/core/base-extension";
import styles from "./styles.scss?inline";
import "@shoelace-style/shoelace/dist/components/alert/alert.js";
import { useUIManager } from "@/stores/ui-store";
import panzoom from "panzoom";

type MermaidPluginState = {
    nodes: Record<
        number,
        {
            pos: number;
            decoration?: Decoration;
            promise?: Promise<RenderResult>;
            renderResult?: RenderResult;
            content: string;
        }
    >;
};

type Config = {
    darkMode: boolean;
    diagramWidth: number;
};

export class MermaidExtension extends BaseExtension {
    private config: Config;
    private renderElement: HTMLElement;
    private mermaidPluginKey = new PluginKey<MermaidPluginState>("mermaid");

    constructor() {
        super();

        const { darkMode } = useUIManager();
        this.config = {
            darkMode: darkMode.value,
            diagramWidth: 1024,
        };

        watch(darkMode, (newValue) => {
            this.updateConfig({
                ...this.config,
                darkMode: newValue,
            });
        });

        // Create a hidden element in which Mermaid can render.
        this.renderElement = document.createElement("div");
        this.renderElement.className = "mermaid-render";
        this.renderElement.style.position = "absolute";
        this.renderElement.style.left = "-9999px";
        this.renderElement.style.top = "-9999px";
        this.renderElement.style.userSelect = "none";
        this.renderElement.style.pointerEvents = "none";
        this.renderElement.style.visibility = "hidden";
        document.body.appendChild(this.renderElement);

        this.mermaid();
    }

    private mermaid = () => {
        mermaid.initialize({
            startOnLoad: false,
            theme: this.config.darkMode ? "dark" : "default",
            securityLevel: "strict",
            gantt: { useWidth: this.config.diagramWidth },
            pie: { useWidth: this.config.diagramWidth },
            fontFamily: "inherit",
            themeVariables: {
                darkMode: this.config.darkMode,
            },
        });
    };

    private updateConfig = (config: Config) => {
        this.config = config;
        this.mermaid();
        const state = this.view?.state;
        if (!state) return;
        this.createMermaidDecorations(state, undefined, true);
    };

    private createMermaidDecorations = (
        state: EditorState,
        nodes?: MermaidPluginState["nodes"],
        forceRecreate?: boolean
    ) => {
        let currentIndex = 0;
        if (!nodes) {
            const pluginState = this.mermaidPluginKey.getState(state);
            nodes = pluginState?.nodes;
        }
        if (!nodes) return {};

        const newNodes: MermaidPluginState["nodes"] = {};

        state.doc.descendants((node, pos) => {
            if (
                node.type.name === "code_block" &&
                node.attrs.params === "mermaid" &&
                node.textContent
            ) {
                const id = `mermaid-diagram-${currentIndex}`;

                if (
                    forceRecreate ||
                    nodes[currentIndex]?.content !== node.textContent
                ) {
                    newNodes[currentIndex] = {
                        pos,
                        content: node.textContent,
                        promise: mermaid.render(
                            id,
                            node.textContent,
                            this.renderElement
                        ),
                    };
                } else if (nodes[currentIndex].pos !== pos) {
                    newNodes[currentIndex] = {
                        ...nodes[currentIndex],
                        pos,
                    };
                    const renderResult = nodes[currentIndex].renderResult;
                    if (renderResult) {
                        newNodes[currentIndex].decoration =
                            this.createMermaidDecoration(
                                pos,
                                id,
                                currentIndex,
                                renderResult.svg
                            );
                    }
                } else {
                    newNodes[currentIndex] = nodes[currentIndex];
                }
                currentIndex++;
            }
        });

        this.nodesToDecorations(newNodes);

        return newNodes;
    };

    /**
     * Await all node promises to be settled and then fire a transaction with the decorations.
     */
    private nodesToDecorations = async (nodes: MermaidPluginState["nodes"]) => {
        const resolvedNodes: MermaidPluginState["nodes"] = {};
        await Promise.allSettled(
            Object.entries(nodes).map(([counter, node]) => {
                if (!node.promise) {
                    return;
                }
                return node.promise
                    .then((renderResult) => {
                        resolvedNodes[+counter] = {
                            ...node,
                            renderResult: renderResult,
                            decoration: this.createMermaidDecoration(
                                node.pos,
                                `mermaid-diagram-${counter}`,
                                +counter,
                                renderResult.svg
                            ),
                        };
                    })
                    .catch((error) => {
                        resolvedNodes[+counter] = {
                            ...node,
                            decoration: this.createMermaidErrorDecoration(
                                node.pos,
                                error,
                                +counter
                            ),
                        };
                    })
                    .finally(() => {
                        // Remove the element from the renderer if it's still there.
                        // It seems to be prefixed with `d` compared to the id we give it.
                        this.renderElement
                            .querySelector(`#dmermaid-diagram-${counter}`)
                            ?.remove();
                    });
            })
        );

        const tr = this.view!.state.tr.setMeta(this.mermaidPluginKey, {
            nodes: resolvedNodes,
        });
        this.view!.dispatch(tr);
    };

    private createMermaidDecoration = (
        pos: number,
        id: string,
        counter: number,
        svg: string
    ): Decoration => {
        return Decoration.widget(
            pos,
            () => {
                const container = document.createElement("div");
                // Required for the mermaid styles.
                container.id = id;
                container.classList.add("mermaid-diagram");

                const previewText = document.createElement("span");
                previewText.classList.add("mermaid-preview-text");
                previewText.innerHTML = "Mermaid preview";

                const diagram = document.createElement("div");
                diagram.innerHTML = svg;

                container.appendChild(previewText);
                container.appendChild(diagram);

                panzoom(diagram, {
                    beforeWheel: function (e) {
                        // Allow wheel-zoom only if meta or ctrl is down.
                        return !(e.metaKey || e.ctrlKey);
                    },
                });

                return container;
            },
            {
                key: `mermaid-diagram-${counter}-${this.config.darkMode ? "dark" : "light"}`,
                side: -1,
            }
        );
    };

    private createMermaidErrorDecoration = (
        pos: number,
        error: string,
        counter: number
    ) => {
        return Decoration.widget(
            pos,
            () => {
                const alert = document.createElement("sl-alert");
                alert.classList.add("mermaid-error");
                alert.variant = "danger";
                alert.open = true;
                alert.innerHTML = `
                    Invalid Mermaid diagram.<br/>
                    <small><i>${error}</i></small>
                `;
                return alert;
            },
            {
                key: `mermaid-error-${counter}`,
                side: -1,
            }
        );
    };

    plugins = () => {
        const mermaidPlugin = new Plugin<MermaidPluginState>({
            key: this.mermaidPluginKey,
            state: {
                init: (_, state) => {
                    const nodes = this.createMermaidDecorations(state, {});
                    return { nodes };
                },
                apply: (tr, value, _oldState, newState) => {
                    const nodes = tr.getMeta(this.mermaidPluginKey)?.nodes;
                    if (tr.docChanged && !nodes) {
                        return {
                            nodes: this.createMermaidDecorations(
                                newState,
                                value.nodes
                            ),
                        };
                    } else if (nodes) {
                        return { nodes };
                    } else {
                        return value;
                    }
                },
            },
            props: {
                decorations: (state) => {
                    const pluginState = this.mermaidPluginKey.getState(state);
                    if (!pluginState?.nodes) {
                        return;
                    }
                    const decorations = Object.values(pluginState.nodes)
                        .filter((node) => node.decoration)
                        .map((node) => node.decoration!);
                    return DecorationSet.create(state.doc, decorations);
                },
            },
        });

        return [mermaidPlugin];
    };

    css = [unsafeCSS(styles)];
}
